<template>
	<Application name="Dashboard" v-model:error-title="errorTitle" :error-detail="errorDetail"
		class="Dashboard" :style="{ background: 'url(\'' + backgroundImage + '\')' }"
	>
		<template #navbar>
			<h1>{{ titleText }}</h1>
			<v-spacer/>
			<v-combobox variant="outlined" hide-details
				v-if="isClientsVisible"
				style="max-width: 200px"
				v-model="selectedClient"
				:items="clients"
				item-title="fields.title.de"
				item-value="fields.title.de"
				density="compact"
			/>

			<div v-if="isProvidersVisible">Operate as</div>
			<v-combobox variant="outlined" hide-details
				v-if="isProvidersVisible"
				style="max-width: 300px"
				v-model="selectedServiceProvider"
				:items="linkedServiceProviders"
				item-title="fields.title.de"
				item-value="fields.title.de"
				density="compact"
			/>
		</template>

		<v-row id="dashboard" align="center" justify="center" style="margin-top: 50px;">
			<v-col>
				<ContractorDashboardWidget v-if="featureEnabled('contract-management')" />
				<Grid
					:draggable="isCustomiseDashboard"
					:sortable="true"
					:items="[ ...internalApps, ...externalApps ]"
					:cell-size="cellSize"
					@sort="sort"
				>
					<template #cell="props">
						<ComponentCardSkeleton v-if="isLoadingTradehubStatus && props.item.fields.link?.de?.includes('/trade-hub')" />
						<ComponentCard
							v-else
							:data-cy="props.item.fields.link.de"
							:componentCard="props.item"
							:isCustomiseDashboard="false"
							:locked="lockedApps.includes(props.item.sys.id)"
							:locked-tooltip="lockedTooltip[props.item.sys.id]"
							@view-component="$emit('view-component',props.item)"
						/>
					</template>
				</Grid>
			</v-col>
		</v-row>
	</Application>
</template>

<script>
import Grid from './grid/Grid.vue'
import ComponentCard from './ComponentCard.vue'
import ComponentCardSkeleton from './ComponentCardSkeleton.vue'
import FreshdeskWidget from '@/utils/FreshdeskWidget'
import Common from '@/mixins/Common.vue'
import eventBus from '@/utils/eventBus.js'
import Application from '../applications/Application.vue'
import isEmpty from 'lodash/isEmpty'
import Loading from '../../mixins/Loading.vue'
import TourOperatorExecutive from '../../../../api2/src/modules/trade-hub/TourOperatorExecutive'
import ContractorDashboardWidget from "../../components/contract/ContractorDashboardWidget.vue"

export default {
	name: 'Dashboard',
	components: { ComponentCard, ComponentCardSkeleton, Grid, Application, ContractorDashboardWidget },
	mixins: [ Common, Loading ],
	data: () => ({
		serviceComponents: [],
		isCustomiseDashboard: false,
		hasUnlockedTradehubApps: false,
		isLoadingTradehubStatus: false
	}),
	computed: {
		lockedApps() {
			let lockedApps = []
			if (!this.hasUnlockedTradehubApps) {
				const ids = this.serviceComponents.filter(x => x.fields.link?.de?.includes('/trade-hub'))?.map(x => x.sys.id)
				lockedApps = ids
			}
			return lockedApps
		},
		lockedTooltip() {
			let tooltips = {}
			const tradeHubAppIds = this.serviceComponents.filter(x => x.fields.link?.de?.includes('/trade-hub'))?.map(x => x.sys.id)
			for(const tradeHubAppId of tradeHubAppIds) {
				tooltips[tradeHubAppId] = this.$t('text.lockedTradeHubAppTooltip')
			}
			return tooltips
		},
		internalApps() {
			return this.serviceComponents.filter(component => !component.fields.link.de.startsWith('/app/'))
		},
		externalApps() {
			return this.serviceComponents.filter(component => component.fields.link.de.startsWith('/app/'))
		},
		backgroundImage() {
			if (!this.$store.state.selectedClient?.fields?.backgroundImage?.de?.fields?.file?.de?.url) {
				return '/myservices_background.jpg'
			}
			return this.$store.state.selectedClient.fields.backgroundImage.de.fields.file.de.url
		},
		isClientsVisible() {
			return this.$store.state.loggedInUser?.fields?.type?.de === 'operator'
				&& !this.$store.state.isImpersonation
				&& this.$store.state.loggedInUser.fields.clients?.de?.length > 1
		},
		isProvidersVisible() {
			if (this.$store.state.loggedInUser?.fields?.type?.de === "serviceprovider") {
				return this.$store.state.loggedInUser?.fields?.serviceProvider?.de?.fields.linkedServiceProviders?.de?.length >= 1
			} else if (this.$store.state.loggedInUser?.fields?.type?.de === 'operator') {
				return this.$store.state.selectedServiceProvider?.fields?.linkedServiceProviders?.de?.length >= 1
			} else {
				return false
			}
		},
		titleText() {
			if (this.$store.state.isImpersonation) {
				return this.$store.state.selectedServiceProvider?.fields.title.de ?? ''
			}
			return ''
		},
		clients() {
			let clients = this.$store.state.loggedInUser.fields.clients.de
			clients.sort((a, b) => { return a.fields.title.de.localeCompare(b.fields.title.de) })
			return clients
		},
		linkedServiceProviders() {
			var linkedServiceProviders = []
			const sp = this.$store.state.loggedInUser?.fields?.type?.de === "serviceprovider" ? this.$store.state.loggedInUser.fields?.serviceProvider?.de : this.$store.state.impersonatedServiceProvider
			
			if (sp) {
				linkedServiceProviders.push(sp)
				if (sp.fields.linkedServiceProviders?.de) {
					for (var lsp of sp.fields.linkedServiceProviders.de) {
						linkedServiceProviders.push(lsp)
					} 
				}
			}
			
			return linkedServiceProviders
		},
		selectedClient: {
			get: function () {
				return this.$store.state.selectedClient;
			},
			set: function (newClient) {
				eventBus.$emit('change-client', newClient);
			}
		},
		selectedServiceProvider: {
			get: function () {
				return this.$store.state.selectedServiceProvider;
			},
			set: function (newServiceProvider) {
				eventBus.$emit('change-service-provider', newServiceProvider);
			}
		},
    cellSize () {
      switch (this.$vuetify.display.name) {
        case 'xs': return 110
        case 'sm': return 150
        case 'md': return 215
        case 'lg': return 225
        case 'xl': return 250
        default: return 300
      }
    }
	},
	
	watch: {
		'$store.state.selectedLocale': function() {
			this.selectedLocale = this.$store.state.selectedLocale
			FreshdeskWidget.methods.install(this.$store)
		}
	},

	async mounted() {
		const maintenanceMode = await this.checkMaintenanceMode()
		FreshdeskWidget.methods.install(this.$store)
		if (maintenanceMode === true) {
			// TODO: is this ok?
			// previously was this, but with keycloak login its not a good idea to redirect to login AT login
			//this.$emit("show-login")
			window.alert('MAINTENANCE ONGOING, PLEASE CHECK BACK LATER!')
		}
		else {
			this.$store.state.filter = null
			await this.$store.commit('setSelectedProduct', null)

			if (this.$store.state.loggedInUser) {
				if (!this.$store.state.loggedInUser?.fields
					|| this.tokenIsExpired(this.$store.state.loggedInUser.kc_token)) {
					console.warn('Dashboard:mounted logged out or expired token -> showLogin')
					this.showLogin()
					return
				}

				if (this.userIsOperator) {
					await this.$store.commit('setSelectedServiceProvider', null);
					await this.$store.commit('setPeakProducts', null)
				}

			if (this.$store.state.loggedInUser.fields?.type?.de === 'operator' && this.$store.state.isImpersonation === true) {
				this.serviceComponents = this.$store.state.selectedServiceProvider.fields?.mainUserAccount?.fields?.applications?.de
			} else {
				this.serviceComponents = this.$store.state.loggedInUser.fields?.applications?.de
			}

			if (!this.userIsOperator) {
				this.isLoadingTradehubStatus = true
				let tourOperatorExecutive = new TourOperatorExecutive(this)
				const serviceProviderId = this.$store.state.loggedInUser?.fields?.serviceProvider?.de?.sys?.id
					|| this.$store.state.selectedServiceProvider?.sys?.id
				this.hasUnlockedTradehubApps = await tourOperatorExecutive.hasUnlockedTradehubApps(this.userEmail, serviceProviderId)
				this.isLoadingTradehubStatus = false
			}

			await this.$store.commit('setComponents', this.serviceComponents);

				if (this.$store.state.initialUrl?.length) {
					await this.loadingOverlay(async () => {
						this.$router.replace(this.$store.state.initialUrl)
						await Promise.all([
							this.initSelectedComponent(this.$store.state.initialUrl),
							this.$store.commit('setInitialUrl', ''),
						])
					}, 'init')
				}
			}

			eventBus.$on('change-client', async (selectedClient) => {
				await this.getClient(selectedClient.sys.id)
			})

			eventBus.$on('change-service-provider', async (selectedServiceProvider) => {
				if (selectedServiceProvider) {
					await this.loadingOverlay(async () => {
						try {
							const res = await this.$httpGet(`/serviceprovider/${selectedServiceProvider.sys.id}`)
							if (selectedServiceProvider.sys.id !== this.$store.state.selectedServiceProvider.sys.id) {
								await this.$store.commit('setIsLinkedProvider', true);
								await this.$store.commit('setIsImpersonation', true);
							}
							else {
								await this.$store.commit('setIsLinkedProvider', false);
								await this.$store.commit('setIsImpersonation', false);
							}

							if (res.serviceProvider.fields.mainUserAccount) {
								this.serviceComponents = res.serviceProvider.fields.mainUserAccount.fields?.applications?.de
								await this.$store.commit('setComponents', this.serviceComponents);
							}

							await this.$store.commit('setSelectedServiceProvider', res.serviceProvider)

							window.setTimeout(() => { this.$router.go() }, 500)
						}
						catch (error) {
							console.error(error)
							console.error(JSON.stringify(error))
							this.errorTitle = this.$t('text.ERROR')

							if (error.response) {
								if (error.response.status === 401) {
									this.$emit('show-login')
								}
								this.errorDetail = error.response.data.error
							}
							else {
								this.errorDetail = error
							}
						}
					}, 'csp')
				}
			})
		}
	},

	created () {
		FreshdeskWidget.methods.prefill('MyServices')
	},

	beforeUnmount () {
		eventBus.$off('change-client')
		eventBus.$off('change-service-provider')
	},
	
	methods: {
		async initSelectedComponent(pathname) {
			const selectedComponent = isEmpty(this.$store.state.selectedComponent) ?
				this.$store.state.serviceComponents?.find(({ fields: { link: { de: url } } }) => [url, `${url}/`].includes(pathname)) : this.$store.state.selectedComponent
			if (!selectedComponent) return
			return this.$store.commit('setSelectedComponent', selectedComponent)
		},
		async checkMaintenanceMode() {
			try {
				return await this.$httpGet(`/maintenance-mode`)
			} catch (error) {
				console.error(error)
			}
		},
		customiseDashboard() {
			this.isCustomiseDashboard = false
		},
		async getClient(clientId) {
			await this.loadingOverlay(async () => {
				try {
					const res = await this.$httpGet(`/client/${clientId}`)
					await this.$store.commit('setSelectedClient', res.client)

					if (this.userIsOperator) {
						let components = await this.$httpGet(`/components/${this.$store.state.selectedClient.sys.id}/serviceprovider`)
						components.sort((a, b) => (a.fields.title[this.selectedLocale] > b.fields.title[this.selectedLocale]) ? 1 : -1)
						await this.$store.commit('setSPApplications', components)
					}

					// HACK: we have to wait for the storage to finish writing before we go away..
					window.setTimeout(() => { this.$router.go() }, 500)
				}
				catch (error) {
					console.error(error)
				}
			}, 'client')
		},
        sort(event) {
          var roles = "";

          for (var item of event.items) {
            roles = `${roles},${item.item.fields.keycloakRole.de}`
          }

          var data = {
            userId: this.$store.state.loggedInUser.sys.id,
            components: roles.substring(1, roles.length)
          }

          this.$httpPost('/dashboard', data).then(async res => {
            var applications = [];

            if (event && event.items) {
              var dashboardItems = event.items;

              if (res.applications && res.applications.length > 0) {
                for (var dashboardItem of dashboardItems) {
                  for (const application of res.applications) {
                    if (dashboardItem.item.fields.keycloakRole.de === application.fields.keycloakRole.de) {
                      applications.push(application)
                    }
                  }
                }
              }
            } else {
              applications = res.applications
            }

            await this.$store.commit('setComponents', applications);
          })
          .catch(error => {
            console.error(error)
          });
        },
		tokenIsExpired(token) {
			try {
				const payload = JSON.parse(atob(token.split('.')[1]))
				const exp = payload.exp * 1000
				return exp < new Date().getTime()
			}
			catch (e) {
				console.warn(e)
				return false
			}
		},
		async showLogin($e) {
			console.warn('showLogin triggered by event', $e)
			this.$root.app.showLogin()
		},
	}
}
</script>

<style scoped>
.main { background-size: cover !important; height: 100%; width: 100%; padding-bottom: 70px; }
/*#card { width: 1200px !important; }*/
.Dashboard { background-size: cover !important; min-height: 100% !important; background-position-x: 50% !important; }
</style>
